// src/main/shell/symbolGrid/SymbolTreeView.ts
// Pure-logic computed view layer over the symbol data source.
// Uses the renderNodeCount pattern (inspired by VS Code's tree widget) for
// O(depth) expand/collapse instead of O(N) flat-array rebuilds.
// LAZY: Only builds TreeNodes for roots + children of expanded nodes.
// No DOM — just data.
//
// PORTED from the agent's SymbolTreeView. The algorithm is byte-identical; the
// only change is the data dependency: instead of `SymbolManager` threaded with
// (gatewayId, controllerId) on every call, it consumes a controller-scoped
// `SymbolGridDataSource` (so calls are `this.source.getChildren(id)` etc).

import { EventEmitter } from './emitter';
import type { SymbolGridDataSource } from './dataSource';
import type { VisibleSymbol, ViewChangedEvent, TreeNode, FilterResult, FlatSymbol } from './types';
import { parseSearchInput, matchesFilters } from './search/searchCommands';
import type { ParsedSearch, FilterContext } from './search/searchCommands';

/**
 * SymbolTreeView — one per controller.
 *
 * Listens to the data source's events and maintains a LAZY tree of TreeNode
 * objects. Only roots and children of expanded nodes are materialized. Un-built
 * children are built on demand when the virtualizer scrolls to them.
 *
 * Events emitted:
 *   'viewChanged' (ViewChangedEvent) — whenever the visible list changes
 */
export class SymbolTreeView extends EventEmitter {
  private source: SymbolGridDataSource;
  private controllerId: string;

  // --- Tree structure ---
  private roots: TreeNode[] = [];
  private nodeBySymbolId: Map<string, TreeNode> = new Map();
  private nodeByNodeId: Map<string, TreeNode> = new Map();
  private parentNodes: Set<TreeNode> = new Set(); // O(1) access to nodes with children
  private totalVisibleCount: number = 0;

  // --- Lazy tree tracking ---
  private knownRootIds: Set<string> = new Set(); // roots already in tree (for incremental discovery)

  // --- Cursor for sequential access (scroll optimization) ---
  private cursor: { node: TreeNode; globalIndex: number } | null = null;
  private cursorValid: boolean = false;

  // --- Expand state (for persistence + toolbar icon) ---
  private _expandAllMode: boolean = true;
  // Restored from localStorage, consumed once during buildRoots/ensureChildrenBuilt
  private restoredCollapsed: Set<string> = new Set();
  private restoredExpanded: Set<string> = new Set();

  // --- Filter state ---
  private filterQuery: string = '';
  private parsedFilter: ParsedSearch | null = null;
  private filterResults: FilterResult[] | null = null; // flat filter mode results

  // --- Column sort state ---
  private sortColumn: string | null = null; // 'name' | 'label' | 'type' | 'access' | 'value' | null
  private sortDirection: 'asc' | 'desc' = 'asc';

  // --- Discovery debounce ---
  private discoveryTimer: ReturnType<typeof setTimeout> | null = null;
  private pendingDiscovery: boolean = false;

  // --- Lifecycle ---
  private active: boolean = false;
  private _lastAccessed: number = Date.now();

  // --- Bound handlers (for clean removal) ---
  private _onSymbolsDiscovered: (data: unknown) => void;
  private _onDiscoveryFinished: (data: unknown) => void;
  private _onSymbolsAdded: (data: unknown) => void;
  private _onSymbolsLost: (data: unknown) => void;
  private _onLabelChanged: (data: unknown) => void;
  private _onSymbolsFetchComplete: (data: unknown) => void;
  private _onSymbolsChunkInserted: (data: unknown) => void;

  constructor(source: SymbolGridDataSource) {
    super();
    this.source = source;
    this.controllerId = source.controllerId;

    // Bind handlers
    this._onSymbolsDiscovered = this.handleSymbolsDiscovered.bind(this);
    this._onDiscoveryFinished = this.handleDiscoveryFinished.bind(this);
    this._onSymbolsAdded = this.handleSymbolsAdded.bind(this);
    this._onSymbolsLost = this.handleSymbolsLost.bind(this);
    this._onLabelChanged = this.handleLabelChanged.bind(this);
    this._onSymbolsFetchComplete = this.handleSymbolsFetchComplete.bind(this);
    this._onSymbolsChunkInserted = this.handleSymbolsChunkInserted.bind(this);

    // Restore persisted state
    this.restoreState();
  }

  /** FilterContext built from the data source (label lookup only). */
  private get filterCtx(): FilterContext {
    return { getLabel: (id) => this.source.getLabel(id) };
  }

  // ============================================================
  // Public API — Ordered List Access
  // ============================================================

  /** Total number of visible symbols */
  getVisibleCount(): number {
    return this.totalVisibleCount;
  }

  /** Get visible symbol data for a range of indices (for viewport rendering) */
  getRange(startIndex: number, endIndex: number): VisibleSymbol[] {
    const result: VisibleSymbol[] = [];
    const end = Math.min(endIndex, this.totalVisibleCount);
    const start = Math.max(0, startIndex);
    if (start >= end) return result;

    // Flat filter mode: direct array access
    if (this.filterResults !== null) {
      for (let i = start; i < end; i++) {
        const vs = this.getFilterResultAsVisibleSymbol(i);
        if (vs) result.push(vs);
      }
      return result;
    }

    // Tree mode: Try cursor for sequential access
    let currentNode: TreeNode | null = null;

    if (this.cursorValid && this.cursor && this.cursor.globalIndex === start) {
      currentNode = this.cursor.node;
    } else {
      currentNode = this.findNodeAtIndex(start);
    }

    if (!currentNode) return result;

    result.push(toVisibleSymbol(currentNode));

    for (let i = start + 1; i < end; i++) {
      currentNode = this.dfsSuccessor(currentNode!);
      if (!currentNode) break;
      result.push(toVisibleSymbol(currentNode));
    }

    // Save cursor for next frame
    if (currentNode) {
      this.cursor = { node: currentNode, globalIndex: end - 1 };
      this.cursorValid = true;
    }

    return result;
  }

  /** Iterate all visible symbol IDs without allocating VisibleSymbol objects.
   *  Callback returns false to break early. */
  forEachVisibleSymbolId(callback: (symbolId: string) => boolean | void): void {
    if (this.totalVisibleCount === 0) return;

    // Flat filter mode
    if (this.filterResults !== null) {
      for (const result of this.filterResults) {
        if (callback(result.symbolId) === false) return;
      }
      return;
    }

    // Tree mode
    let node = this.findNodeAtIndex(0);
    while (node) {
      if (callback(node.symbolId) === false) return;
      node = this.dfsSuccessor(node);
    }
  }

  /** Get a single symbol's view data by index */
  getSymbolAtIndex(index: number): VisibleSymbol | undefined {
    if (index < 0 || index >= this.totalVisibleCount) return undefined;

    // Flat filter mode: O(1) array index
    if (this.filterResults !== null) {
      return this.getFilterResultAsVisibleSymbol(index);
    }

    // Tree mode
    const node = this.findNodeAtIndex(index);
    return node ? toVisibleSymbol(node) : undefined;
  }

  /** Build symbolId→index map for all visible symbols. O(visibleCount) via DFS.
   *  Used by expand/collapse-all animation for O(1) per-row lookup.
   *  With lazy tree, only walks built nodes. */
  buildIndexMap(): Map<string, number> {
    const map = new Map<string, number>();

    if (this.filterResults !== null) {
      for (let i = 0; i < this.filterResults.length; i++) {
        map.set(this.filterResults[i].symbolId, i);
      }
      return map;
    }

    let idx = 0;
    for (const root of this.roots) {
      this.buildIndexMapRecursive(root, map, idx);
      idx += root.renderNodeCount;
    }
    return map;
  }

  /** Get the visible index of a symbol ID (for scroll-to). Returns -1 if not visible. */
  getIndexOfSymbol(symbolId: string): number {
    // Filter mode
    if (this.filterResults !== null) {
      for (let i = 0; i < this.filterResults.length; i++) {
        if (this.filterResults[i].symbolId === symbolId) return i;
      }
      return -1;
    }

    // Tree mode
    const node = this.nodeBySymbolId.get(symbolId);
    if (!node || node.renderNodeCount === 0) return -1;
    if (!this.isNodeVisible(node)) return -1;
    return this.getIndexOfNode(node);
  }

  // ============================================================
  // Public API — Expand / Collapse
  // ============================================================

  /** Toggle a single node's expand state */
  toggleExpand(nodeId: string): void {
    // No-op in filter mode (flat list, no expand/collapse)
    if (this.filterResults !== null) return;

    const node = this.nodeByNodeId.get(nodeId);
    if (!node || !node.hasChildren) return;

    const wasExpanded = node.expanded;

    // Compute splice index BEFORE modifying counts
    const spliceIndex = this.getIndexOfNode(node) + 1;
    const oldRenderNodeCount = node.renderNodeCount;

    // Flip expanded flag
    node.expanded = !wasExpanded;

    if (wasExpanded) {
      // Collapsing: renderNodeCount becomes 1 (just self) — O(1)
      node.renderNodeCount = 1;
      const removeCount = oldRenderNodeCount - 1;

      this.propagateRenderNodeCountUp(node);
      this.invalidateCursor();

      if (removeCount > 0) {
        this.emitViewChanged({
          type: 'splice',
          spliceIndex,
          removeCount,
          insertCount: 0,
          visibleCount: this.totalVisibleCount,
        });
      }
    } else {
      // Expanding: build children if not yet built
      if (!node.childrenBuilt) {
        this.ensureChildrenBuilt(node);
      }

      this.recomputeRenderNodeCountLazy(node);
      const insertCount = node.renderNodeCount - 1;

      this.propagateRenderNodeCountUp(node);
      this.invalidateCursor();

      if (insertCount > 0) {
        this.emitViewChanged({
          type: 'splice',
          spliceIndex,
          removeCount: 0,
          insertCount,
          visibleCount: this.totalVisibleCount,
        });
      }
    }

    // Track for persistence
    if (this._expandAllMode) {
      if (!node.expanded) {
        this.restoredCollapsed.add(nodeId);
      } else {
        this.restoredCollapsed.delete(nodeId);
      }
    } else {
      if (node.expanded) {
        this.restoredExpanded.add(nodeId);
      } else {
        this.restoredExpanded.delete(nodeId);
      }
    }

    this.saveState();
  }

  /** Expand all / collapse all */
  setExpandAll(expanded: boolean): void {
    this._expandAllMode = expanded;
    this.restoredCollapsed.clear();
    this.restoredExpanded.clear();

    // Filter mode: just save state, filter results don't change
    if (this.filterResults !== null) {
      this.invalidateCursor();
      this.emitViewChanged({ type: 'full', visibleCount: this.totalVisibleCount });
      this.saveState();
      return;
    }

    if (!expanded) {
      // Collapse-all: O(P) — every parent becomes collapsed
      for (const node of this.parentNodes) {
        node.expanded = false;
        node.renderNodeCount = 1;
      }
      this.totalVisibleCount = this.roots.length;
    } else {
      // Expand-all: mark all built parents as expanded
      for (const node of this.parentNodes) {
        node.expanded = true;
      }

      // Set renderNodeCount = subtreeSize for all roots (no child building needed)
      this.totalVisibleCount = 0;
      for (const root of this.roots) {
        root.renderNodeCount = this.source.getSubtreeSize(root.symbolId);
        this.totalVisibleCount += root.renderNodeCount;
      }
      // For built intermediate nodes, also update from subtreeSize
      for (const node of this.parentNodes) {
        if (node.parent) {
          // non-root
          node.renderNodeCount = this.source.getSubtreeSize(node.symbolId);
        }
      }
    }

    this.invalidateCursor();
    this.emitViewChanged({ type: 'full', visibleCount: this.totalVisibleCount });
    this.saveState();
  }

  /** Check if a node is expanded */
  isExpanded(nodeId: string): boolean {
    const node = this.nodeByNodeId.get(nodeId);
    return node ? node.expanded : false;
  }

  /** Whether we're in expand-all mode */
  get expandAllMode(): boolean {
    return this._expandAllMode;
  }

  // ============================================================
  // Public API — Search / Filter (Flat Filter Mode)
  // ============================================================

  /** Set the search/filter query. Switches to flat filter mode when active. */
  setFilter(query: string): void {
    this.filterQuery = query;

    if (!query.trim()) {
      this.parsedFilter = null;
      this.filterResults = null;
      // Re-enter tree mode: recompute visible count from tree
      this.recomputeTotalVisibleFromRoots();
      this.invalidateCursor();
      this.emitViewChanged({ type: 'full', visibleCount: this.totalVisibleCount });
      this.saveState();
      return;
    }

    const prevResults = this.filterResults;
    this.parsedFilter = parseSearchInput(query);

    // Compute flat filter results from the data source (no TreeNodes needed)
    this.filterResults = this.computeFlatFilterResults(prevResults);
    this.totalVisibleCount = this.filterResults.length;

    this.invalidateCursor();
    this.emitViewChanged({ type: 'full', visibleCount: this.totalVisibleCount });
    this.saveState();
  }

  /** Get current filter query */
  getFilter(): string {
    return this.filterQuery;
  }

  // ============================================================
  // Public API — Column Sorting
  // ============================================================

  /** Get current sort state */
  getSort(): { column: string | null; direction: 'asc' | 'desc' } {
    return { column: this.sortColumn, direction: this.sortDirection };
  }

  /**
   * Set column sort. Passing the same column cycles: asc → desc → none.
   * Passing a different column starts at asc.
   * Passing null clears the sort (restores natural order).
   */
  setSort(column: string | null): void {
    if (column === null) {
      if (this.sortColumn === null) return;
      this.sortColumn = null;
      this.sortDirection = 'asc';
    } else if (column === this.sortColumn) {
      if (this.sortDirection === 'asc') {
        this.sortDirection = 'desc';
      } else {
        this.sortColumn = null;
        this.sortDirection = 'asc';
      }
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }

    // In filter mode: re-sort filter results
    if (this.filterResults !== null) {
      this.sortFilterResults();
    } else {
      // Tree mode: re-sort built children
      this.applySortToTree();
    }

    this.cursorValid = false;
    this.saveState();
    this.emitViewChanged({ type: 'full', visibleCount: this.totalVisibleCount });
  }

  /**
   * Re-sort all BUILT children arrays (and roots) based on the active sort column.
   * With lazy tree, only sorts nodes that have been materialized.
   */
  private applySortToTree(): void {
    const comparator = this.buildComparator();

    // Sort roots
    this.roots.sort(comparator);
    for (let i = 0; i < this.roots.length; i++) {
      this.roots[i].childIndex = i;
    }

    // Recursively sort only built children
    this.sortBuiltChildrenRecursive(this.roots, comparator);
  }

  private sortBuiltChildrenRecursive(nodes: TreeNode[], comparator: (a: TreeNode, b: TreeNode) => number): void {
    for (const node of nodes) {
      if (node.childrenBuilt && node.children.length > 1) {
        node.children.sort(comparator);
        for (let i = 0; i < node.children.length; i++) {
          node.children[i].childIndex = i;
        }
      }
      if (node.childrenBuilt && node.children.length > 0) {
        this.sortBuiltChildrenRecursive(node.children, comparator);
      }
    }
  }

  /** Sort filter results by the current sort column */
  private sortFilterResults(): void {
    if (!this.filterResults || this.filterResults.length === 0) return;

    const col = this.sortColumn;
    const dir = this.sortDirection === 'asc' ? 1 : -1;
    const src = this.source;

    if (!col) {
      // Default: sort by DFS path
      this.filterResults.sort((a, b) => naturalSort(a.sortPath, b.sortPath));
      return;
    }

    // Build sort keys for filter results
    const keys = new Map<string, SortKey>();
    for (const r of this.filterResults) {
      const sym = src.getSymbol(r.symbolId);
      const key: SortKey = { str: '', num: NaN, empty: false, typeGroup: 0 };

      switch (col) {
        case 'name':
          key.str = r.symbolId;
          break;
        case 'label': {
          const label = src.getLabel(r.symbolId);
          key.str = label || '';
          key.empty = !label;
          break;
        }
        case 'type':
          key.str = normalizeType(sym?.type);
          break;
        case 'access':
          key.str = sym?.permission || 'r';
          break;
        case 'value': {
          const val = src.getValue(r.symbolId);
          const isNull = val === null || val === undefined;
          key.empty = isNull;
          key.typeGroup = typeGroupOrder(sym?.type);
          if (!isNull) {
            const n = Number(val);
            if (!isNaN(n)) key.num = n;
            else key.str = String(val);
          }
          break;
        }
      }
      keys.set(r.symbolId, key);
    }

    // Use same comparator logic as tree sort
    this.filterResults.sort((a, b) => {
      const ka = keys.get(a.symbolId)!;
      const kb = keys.get(b.symbolId)!;

      switch (col) {
        case 'name':
          return dir * naturalSort(a.symbolId, b.symbolId);
        case 'label': {
          if (ka.empty && kb.empty) return naturalSort(a.symbolId, b.symbolId);
          if (ka.empty) return 1;
          if (kb.empty) return -1;
          const cmp = ka.str.localeCompare(kb.str, undefined, { sensitivity: 'base' });
          return cmp !== 0 ? dir * cmp : naturalSort(a.symbolId, b.symbolId);
        }
        case 'type':
        case 'access': {
          const cmp = ka.str.localeCompare(kb.str);
          return cmp !== 0 ? dir * cmp : naturalSort(a.symbolId, b.symbolId);
        }
        case 'value': {
          if (ka.empty && kb.empty) return naturalSort(a.symbolId, b.symbolId);
          if (ka.empty) return 1;
          if (kb.empty) return -1;
          if (ka.typeGroup !== kb.typeGroup) return dir * (ka.typeGroup - kb.typeGroup);
          if (!isNaN(ka.num) && !isNaN(kb.num)) {
            return ka.num !== kb.num ? dir * (ka.num - kb.num) : naturalSort(a.symbolId, b.symbolId);
          }
          const cmp = ka.str.localeCompare(kb.str, undefined, { sensitivity: 'base' });
          return cmp !== 0 ? dir * cmp : naturalSort(a.symbolId, b.symbolId);
        }
        default:
          return naturalSort(a.sortPath, b.sortPath);
      }
    });
  }

  /**
   * Pre-computed sort key for a single node.
   * Using a flat struct avoids per-comparison Map/source lookups.
   */
  private buildSortKeys(): Map<string, SortKey> {
    const col = this.sortColumn;
    const src = this.source;
    const keys = new Map<string, SortKey>();

    // Only build keys for materialized nodes (lazy tree)
    for (const [symbolId] of this.nodeBySymbolId) {
      const sym = src.getSymbol(symbolId);
      const key: SortKey = { str: '', num: NaN, empty: false, typeGroup: 0 };

      switch (col) {
        case 'name':
          key.str = symbolId;
          break;
        case 'label': {
          const label = src.getLabel(symbolId);
          key.str = label || '';
          key.empty = !label;
          break;
        }
        case 'type':
          key.str = normalizeType(sym?.type);
          break;
        case 'access':
          key.str = sym?.permission || 'r';
          break;
        case 'value': {
          const val = src.getValue(symbolId);
          const isNull = val === null || val === undefined;
          key.empty = isNull;
          key.typeGroup = typeGroupOrder(sym?.type);
          if (!isNull) {
            const n = Number(val);
            if (!isNaN(n)) {
              key.num = n;
            } else {
              key.str = String(val);
            }
          }
          break;
        }
      }

      keys.set(symbolId, key);
    }

    return keys;
  }

  /**
   * Build a comparator function for the current sort state.
   * Falls back to naturalSort when sortColumn is null.
   */
  private buildComparator(): (a: TreeNode, b: TreeNode) => number {
    const col = this.sortColumn;
    const dir = this.sortDirection === 'asc' ? 1 : -1;

    if (!col) {
      return (a, b) => naturalSort(a.symbolId, b.symbolId);
    }

    const keys = this.buildSortKeys();

    switch (col) {
      case 'name':
        return (a, b) => dir * naturalSort(a.symbolId, b.symbolId);

      case 'label':
        return (a, b) => {
          const ka = keys.get(a.symbolId);
          const kb = keys.get(b.symbolId);
          if (!ka || !kb) return naturalSort(a.symbolId, b.symbolId);
          if (ka.empty && kb.empty) return naturalSort(a.symbolId, b.symbolId);
          if (ka.empty) return 1;
          if (kb.empty) return -1;
          const cmp = ka.str.localeCompare(kb.str, undefined, { sensitivity: 'base' });
          return cmp !== 0 ? dir * cmp : naturalSort(a.symbolId, b.symbolId);
        };

      case 'type':
        return (a, b) => {
          const ka = keys.get(a.symbolId);
          const kb = keys.get(b.symbolId);
          if (!ka || !kb) return naturalSort(a.symbolId, b.symbolId);
          const cmp = ka.str.localeCompare(kb.str);
          return cmp !== 0 ? dir * cmp : naturalSort(a.symbolId, b.symbolId);
        };

      case 'access':
        return (a, b) => {
          const ka = keys.get(a.symbolId);
          const kb = keys.get(b.symbolId);
          if (!ka || !kb) return naturalSort(a.symbolId, b.symbolId);
          const cmp = ka.str.localeCompare(kb.str);
          return cmp !== 0 ? dir * cmp : naturalSort(a.symbolId, b.symbolId);
        };

      case 'value':
        return (a, b) => {
          const ka = keys.get(a.symbolId);
          const kb = keys.get(b.symbolId);
          if (!ka || !kb) return naturalSort(a.symbolId, b.symbolId);
          if (ka.empty && kb.empty) return naturalSort(a.symbolId, b.symbolId);
          if (ka.empty) return 1;
          if (kb.empty) return -1;
          if (ka.typeGroup !== kb.typeGroup) return dir * (ka.typeGroup - kb.typeGroup);
          if (!isNaN(ka.num) && !isNaN(kb.num)) {
            return ka.num !== kb.num ? dir * (ka.num - kb.num) : naturalSort(a.symbolId, b.symbolId);
          }
          const cmp = ka.str.localeCompare(kb.str, undefined, { sensitivity: 'base' });
          return cmp !== 0 ? dir * cmp : naturalSort(a.symbolId, b.symbolId);
        };

      default:
        return (a, b) => naturalSort(a.symbolId, b.symbolId);
    }
  }

  // ============================================================
  // Public API — Accessors
  // ============================================================

  get lastAccessed(): number {
    return this._lastAccessed;
  }

  touchAccess(): void {
    this._lastAccessed = Date.now();
  }

  getControllerId(): string {
    return this.controllerId;
  }

  /** Get the nodeId for a symbolId */
  getNodeId(symbolId: string): string {
    const node = this.nodeBySymbolId.get(symbolId);
    return node ? node.nodeId : symbolId;
  }

  /** Get a TreeNode by its nodeId */
  getNodeByNodeId(nodeId: string): TreeNode | undefined {
    return this.nodeByNodeId.get(nodeId);
  }

  /** Get the level (depth) for a symbolId */
  getLevel(symbolId: string): number {
    const node = this.nodeBySymbolId.get(symbolId);
    if (node) return node.level;
    // Fallback to source depth for un-built nodes
    return this.source.getDepth(symbolId);
  }

  // ============================================================
  // Public API — Lifecycle
  // ============================================================

  /** Start listening to source events */
  activate(): void {
    if (this.active) return;
    this.active = true;

    this.source.on('symbolsDiscovered', this._onSymbolsDiscovered);
    this.source.on('symbolDiscoveryFinished', this._onDiscoveryFinished);
    this.source.on('symbolsAdded', this._onSymbolsAdded);
    this.source.on('symbolsLost', this._onSymbolsLost);
    this.source.on('symbolLabelChanged', this._onLabelChanged);
    this.source.on('symbolsFetchComplete', this._onSymbolsFetchComplete);
    this.source.on('symbolsChunkInserted', this._onSymbolsChunkInserted);

    // Build from current source state (may already have data)
    this.fullRebuild();
  }

  /** Stop listening */
  deactivate(): void {
    if (!this.active) return;
    this.active = false;

    this.source.off('symbolsDiscovered', this._onSymbolsDiscovered);
    this.source.off('symbolDiscoveryFinished', this._onDiscoveryFinished);
    this.source.off('symbolsAdded', this._onSymbolsAdded);
    this.source.off('symbolsLost', this._onSymbolsLost);
    this.source.off('symbolLabelChanged', this._onLabelChanged);
    this.source.off('symbolsFetchComplete', this._onSymbolsFetchComplete);
    this.source.off('symbolsChunkInserted', this._onSymbolsChunkInserted);

    if (this.discoveryTimer) {
      clearTimeout(this.discoveryTimer);
      this.discoveryTimer = null;
    }
  }

  /** Full cleanup */
  destroy(): void {
    this.deactivate();
    this.roots = [];
    this.nodeBySymbolId.clear();
    this.nodeByNodeId.clear();
    this.parentNodes.clear();
    this.knownRootIds.clear();
    this.totalVisibleCount = 0;
    this.cursor = null;
    this.cursorValid = false;
    this.restoredCollapsed.clear();
    this.restoredExpanded.clear();
    this.filterResults = null;
    this.removeAllListeners();
  }

  // ============================================================
  // Event Handlers
  // ============================================================

  private handleSymbolsDiscovered(): void {
    this.pendingDiscovery = true;
    this.scheduleDiscoveryRebuild();
  }

  private handleDiscoveryFinished(): void {
    // Flush immediately
    if (this.discoveryTimer) {
      clearTimeout(this.discoveryTimer);
      this.discoveryTimer = null;
    }
    this.pendingDiscovery = false;
    this.handleDiscoveryTick();
  }

  private handleSymbolsFetchComplete(): void {
    this.fullRebuild();
  }

  private handleSymbolsAdded(): void {
    this.handleDiscoveryTick();
  }

  private handleSymbolsChunkInserted(): void {
    this.handleDiscoveryTick();
  }

  /**
   * Handle symbolsLost: remove deleted nodes, re-parent orphaned children,
   * and recompute renderNodeCounts.
   *
   * Performance:
   *   Common case (delete + all children): O(D + siblings + depth + roots)
   *   Re-parenting case: O(children + built_subtree + depth)
   *   No full-tree walks in any path.
   */
  private handleSymbolsLost(data: unknown): void {
    const lostIds: string[] = (data as { symbols?: string[] })?.symbols || [];
    if (lostIds.length === 0) return;

    const lostSet = new Set(lostIds);
    let changed = false;
    let hasUnbuiltDeletions = false;
    const affectedParents = new Set<TreeNode>();
    let needsRootReindex = false;

    for (const symbolId of lostIds) {
      const node = this.nodeBySymbolId.get(symbolId);
      if (!node) {
        // Symbol was never materialized as a TreeNode (lazy un-built).
        // Flag so we refresh stale renderNodeCounts for un-built expanded ancestors.
        hasUnbuiltDeletions = true;
        continue;
      }

      const parent = node.parent;

      // --- Re-parent built children NOT in lostSet to grandparent (or roots) ---
      // The source already re-parented them in its hierarchy; mirror that here.
      if (node.childrenBuilt) {
        for (const child of node.children) {
          if (lostSet.has(child.symbolId)) continue;

          this.nodeByNodeId.delete(child.nodeId);

          if (parent) {
            child.parent = parent;
            child.level = parent.level + 1;
            child.nodeId = parent.nodeId + '/' + child.symbolId;
            parent.children.push(child);
          } else {
            child.parent = null;
            child.level = 0;
            child.nodeId = child.symbolId;
            this.roots.push(child);
            this.knownRootIds.add(child.symbolId);
            needsRootReindex = true;
          }

          child._visibleCache = null;
          this.nodeByNodeId.set(child.nodeId, child);

          // Recursively fix paths + levels for already-built descendants
          if (child.childrenBuilt && child.children.length > 0) {
            this.rebuildDescendantPaths(child);
          }
        }
      }

      // --- Remove node from parent's children or roots ---
      // Skip parent manipulation if parent is also being deleted (it'll be cleaned up
      // when we process it, and its re-parenting handles cascading correctly).
      if (parent) {
        if (!lostSet.has(parent.symbolId)) {
          const idx = parent.children.indexOf(node);
          if (idx !== -1) parent.children.splice(idx, 1);
          affectedParents.add(parent);
        }
      } else {
        const idx = this.roots.indexOf(node);
        if (idx !== -1) {
          this.roots.splice(idx, 1);
          needsRootReindex = true;
        }
      }

      // --- Clean up maps ---
      this.nodeBySymbolId.delete(symbolId);
      this.nodeByNodeId.delete(node.nodeId);
      this.parentNodes.delete(node);
      this.knownRootIds.delete(symbolId);
      changed = true;
    }

    if (!changed && !hasUnbuiltDeletions) return;

    // --- Sync affected parents ---
    // Re-sort children (re-parented children were appended), update hasChildren,
    // and build any un-built re-parented children that the source moved here.
    const comparator = this.buildComparator();
    for (const parent of affectedParents) {
      // Detect un-built re-parented children: the source may have re-parented children
      // from a deleted node with childrenBuilt=false to this (built) parent.
      if (parent.childrenBuilt) {
        const smChildren = this.source.getChildren(parent.symbolId);
        if (smChildren.size > parent.children.length) {
          const builtIds = new Set(parent.children.map((c) => c.symbolId));
          for (const childId of smChildren) {
            if (builtIds.has(childId)) continue;
            const childChildIds = this.source.getChildren(childId);
            const hasChildren = childChildIds.size > 0;
            const childNodeId = parent.nodeId + '/' + childId;
            const expanded = this._expandAllMode
              ? hasChildren
                ? !this.restoredCollapsed.has(childNodeId)
                : false
              : hasChildren
                ? this.restoredExpanded.has(childNodeId)
                : false;
            const childNode: TreeNode = {
              symbolId: childId,
              nodeId: childNodeId,
              level: parent.level + 1,
              parent,
              children: [],
              childrenBuilt: false,
              childIndex: 0,
              expanded,
              hasChildren,
              renderNodeCount: 1,
              matchesFilter: false,
              _visibleCache: null,
            };
            if (expanded && hasChildren && this._expandAllMode) {
              childNode.renderNodeCount = this.source.getSubtreeSize(childId);
            }
            this.nodeBySymbolId.set(childId, childNode);
            this.nodeByNodeId.set(childNodeId, childNode);
            if (hasChildren) this.parentNodes.add(childNode);
            parent.children.push(childNode);
          }
        }
      }

      // Re-sort and re-index children
      parent.children.sort(comparator);
      for (let i = 0; i < parent.children.length; i++) {
        parent.children[i].childIndex = i;
      }

      // Update hasChildren
      if (parent.children.length === 0 && parent.childrenBuilt) {
        const smChildren = this.source.getChildren(parent.symbolId);
        if (smChildren.size === 0) {
          parent.hasChildren = false;
          parent.expanded = false;
          this.parentNodes.delete(parent);
        }
      }

      parent._visibleCache = null;
    }

    // --- Refresh un-built expanded nodes' renderNodeCounts ---
    // Deletions of non-materialized symbols leave stale subtreeSize-based counts.
    // O(parentNodes) but only when un-built deletions occurred.
    if (hasUnbuiltDeletions) {
      for (const node of this.parentNodes) {
        if (node.expanded && !node.childrenBuilt) {
          node.renderNodeCount = this.source.getSubtreeSize(node.symbolId);
        }
      }
    }

    // --- Recompute renderNodeCounts for affected parents and propagate up ---
    for (const parent of affectedParents) {
      this.recomputeRenderNodeCountLazy(parent);
      this.propagateCountsUp(parent);
    }

    // Re-sort and re-index roots if modified
    if (needsRootReindex) {
      this.roots.sort(comparator);
      for (let i = 0; i < this.roots.length; i++) {
        this.roots[i].childIndex = i;
      }
    }

    this.recomputeTotalVisibleFromRoots();
    this.invalidateCursor();

    if (this.filterResults !== null) {
      this.filterResults = this.computeFlatFilterResults();
      this.totalVisibleCount = this.filterResults.length;
    }

    this.emitViewChanged({ type: 'full', visibleCount: this.totalVisibleCount });
  }

  /**
   * Recursively rebuild nodeId, level, and clear _visibleCache for built descendants.
   * Only visits materialized TreeNodes — O(built_subtree_size).
   */
  private rebuildDescendantPaths(parent: TreeNode): void {
    for (const child of parent.children) {
      this.nodeByNodeId.delete(child.nodeId);
      child.nodeId = parent.nodeId + '/' + child.symbolId;
      child.level = parent.level + 1;
      child._visibleCache = null;
      this.nodeByNodeId.set(child.nodeId, child);
      if (child.childrenBuilt && child.children.length > 0) {
        this.rebuildDescendantPaths(child);
      }
    }
  }

  private handleLabelChanged(): void {
    // In flat filter mode with /label filter, recompute results
    if (this.parsedFilter && this.filterQuery.includes('/label') && this.filterResults !== null) {
      this.filterResults = this.computeFlatFilterResults();
      this.totalVisibleCount = this.filterResults.length;
      this.invalidateCursor();
      this.emitViewChanged({ type: 'full', visibleCount: this.totalVisibleCount });
    }
  }

  /**
   * Discovery debounce: schedule a rebuild tick in 500ms.
   */
  private scheduleDiscoveryRebuild(): void {
    if (this.discoveryTimer) return;

    this.discoveryTimer = setTimeout(() => {
      this.discoveryTimer = null;
      if (this.pendingDiscovery) {
        this.pendingDiscovery = false;
        this.handleDiscoveryTick();
      }
    }, 500);
  }

  // ============================================================
  // Core: Lazy Tree Construction
  // ============================================================

  /**
   * Full rebuild: build roots only (lazy). Called on initial load, fetchComplete, etc.
   */
  private fullRebuild(): void {
    this.buildRoots();

    if (this.sortColumn) {
      this.applySortToTree();
    }

    // If filter active, compute flat results
    if (this.parsedFilter) {
      this.filterResults = this.computeFlatFilterResults();
      this.totalVisibleCount = this.filterResults.length;
    }

    this.invalidateCursor();
    this.emitViewChanged({ type: 'full', visibleCount: this.totalVisibleCount });
  }

  /**
   * Build only root-level TreeNodes. Children are built lazily via ensureChildrenBuilt().
   * O(R) where R = number of roots, instead of O(N) for the full tree.
   */
  private buildRoots(): void {
    this.roots = [];
    this.nodeBySymbolId.clear();
    this.nodeByNodeId.clear();
    this.parentNodes.clear();
    this.knownRootIds.clear();

    const rootIds = this.source.getRootSymbols();
    if (!rootIds || rootIds.size === 0) {
      this.totalVisibleCount = 0;
      return;
    }

    const sortedRootIds = [...rootIds].sort(naturalSort);

    for (let ri = 0; ri < sortedRootIds.length; ri++) {
      const rootId = sortedRootIds[ri];
      const childIds = this.source.getChildren(rootId);
      const hasChildren = childIds.size > 0;

      let expanded: boolean;
      if (this._expandAllMode) {
        expanded = hasChildren ? !this.restoredCollapsed.has(rootId) : false;
      } else {
        expanded = hasChildren ? this.restoredExpanded.has(rootId) : false;
      }

      const node: TreeNode = {
        symbolId: rootId,
        nodeId: rootId, // Roots have simple nodeId = symbolId
        level: 0,
        parent: null,
        children: [],
        childrenBuilt: false,
        childIndex: ri,
        expanded,
        hasChildren,
        renderNodeCount: 1, // Updated below
        matchesFilter: false,
        _visibleCache: null,
      };

      this.nodeBySymbolId.set(rootId, node);
      this.nodeByNodeId.set(rootId, node);
      this.knownRootIds.add(rootId);
      if (hasChildren) this.parentNodes.add(node);

      // Compute renderNodeCount
      if (expanded && hasChildren) {
        if (this._expandAllMode) {
          // Expand-all: use subtreeSize from the source (no child building needed)
          node.renderNodeCount = this.source.getSubtreeSize(rootId);
        } else {
          // Normal expanded: must build children to get accurate count
          this.ensureChildrenBuilt(node);
          this.recomputeRenderNodeCountLazy(node);
        }
      }

      this.roots.push(node);
    }

    this.recomputeTotalVisibleFromRoots();
  }

  /**
   * Build children for a node on demand. Called when the virtualizer needs to
   * walk into this node's children (expand, scroll, etc.)
   * O(children_count) — only builds direct children, not the full subtree.
   */
  private ensureChildrenBuilt(node: TreeNode): void {
    if (node.childrenBuilt || !node.hasChildren) return;

    const childIds = this.source.getChildren(node.symbolId);
    if (childIds.size === 0) {
      node.childrenBuilt = true;
      return;
    }

    const childNodes: TreeNode[] = [];

    for (const childId of childIds) {
      const childChildIds = this.source.getChildren(childId);
      const hasChildren = childChildIds.size > 0;
      const childNodeId = node.nodeId + '/' + childId;

      let expanded: boolean;
      if (this._expandAllMode) {
        expanded = hasChildren ? !this.restoredCollapsed.has(childNodeId) : false;
      } else {
        expanded = hasChildren ? this.restoredExpanded.has(childNodeId) : false;
      }

      const childNode: TreeNode = {
        symbolId: childId,
        nodeId: childNodeId,
        level: node.level + 1,
        parent: node,
        children: [],
        childrenBuilt: false,
        childIndex: 0, // set after sort
        expanded,
        hasChildren,
        renderNodeCount: 1, // Updated below
        matchesFilter: false,
        _visibleCache: null,
      };

      this.nodeBySymbolId.set(childId, childNode);
      this.nodeByNodeId.set(childNodeId, childNode);
      if (hasChildren) this.parentNodes.add(childNode);
      childNodes.push(childNode);
    }

    // Sort children
    const comparator = this.buildComparator();
    childNodes.sort(comparator);
    for (let i = 0; i < childNodes.length; i++) {
      childNodes[i].childIndex = i;
    }

    node.children = childNodes;
    node.childrenBuilt = true;

    // Recursively build children of expanded children
    for (const child of childNodes) {
      if (child.expanded && child.hasChildren) {
        if (this._expandAllMode) {
          // In expand-all mode, use subtreeSize for un-built expanded nodes
          child.renderNodeCount = this.source.getSubtreeSize(child.symbolId);
        } else {
          this.ensureChildrenBuilt(child);
          this.recomputeRenderNodeCountLazy(child);
        }
      }
    }

    // Recompute this node's renderNodeCount
    this.recomputeRenderNodeCountLazy(node);
  }

  // ============================================================
  // Core: Incremental Discovery
  // ============================================================

  /**
   * Incremental update on discovery tick. Instead of rebuilding the entire tree,
   * only inserts new roots and updates expanded parents with new children.
   * O(new_roots + expanded_parents) instead of O(N_total).
   */
  private handleDiscoveryTick(): void {
    // If filter active, recompute filter results — BUT defer during streaming.
    // computeFlatFilterResults scans ALL symbols (O(N)), and during streaming this
    // runs per-chunk which creates O(N×C) total work. The filter will be recomputed
    // once on symbolsFetchComplete → fullRebuild, which already calls computeFlatFilterResults.
    if (this.filterResults !== null) {
      if (this.source.isStreamingSymbols()) {
        // During streaming: skip the expensive recompute, just emit a view change
        // so the badge/count updates. The filter applies on stream completion.
        this.emitViewChanged({ type: 'full', visibleCount: this.totalVisibleCount });
        return;
      }
      this.filterResults = this.computeFlatFilterResults();
      this.totalVisibleCount = this.filterResults.length;
      this.invalidateCursor();
      this.emitViewChanged({ type: 'full', visibleCount: this.totalVisibleCount });
      return;
    }

    const smRoots = this.source.getRootSymbols();
    if (!smRoots || smRoots.size === 0) {
      // If we have stale roots but the source is empty (e.g., after clear), do full rebuild
      if (this.roots.length > 0) {
        this.fullRebuild();
      }
      return;
    }

    let countChanged = false;
    const comparator = this.buildComparator();

    // Find new roots not yet in the tree
    const newRootIds: string[] = [];
    for (const rootId of smRoots) {
      if (!this.knownRootIds.has(rootId)) {
        newRootIds.push(rootId);
      }
    }

    // Insert new roots in sorted position
    if (newRootIds.length > 0) {
      for (const rootId of newRootIds) {
        const childIds = this.source.getChildren(rootId);
        const hasChildren = childIds.size > 0;

        let expanded: boolean;
        if (this._expandAllMode) {
          expanded = hasChildren ? !this.restoredCollapsed.has(rootId) : false;
        } else {
          expanded = hasChildren ? this.restoredExpanded.has(rootId) : false;
        }

        const node: TreeNode = {
          symbolId: rootId,
          nodeId: rootId,
          level: 0,
          parent: null,
          children: [],
          childrenBuilt: false,
          childIndex: 0,
          expanded,
          hasChildren,
          renderNodeCount: 1,
          matchesFilter: false,
          _visibleCache: null,
        };

        if (expanded && hasChildren && this._expandAllMode) {
          node.renderNodeCount = this.source.getSubtreeSize(rootId);
        }

        this.nodeBySymbolId.set(rootId, node);
        this.nodeByNodeId.set(rootId, node);
        this.knownRootIds.add(rootId);
        if (hasChildren) this.parentNodes.add(node);

        // Binary insert to maintain sort order
        let insertIdx = this.roots.length;
        for (let i = 0; i < this.roots.length; i++) {
          if (comparator(node, this.roots[i]) < 0) {
            insertIdx = i;
            break;
          }
        }
        this.roots.splice(insertIdx, 0, node);
        // Re-index from insertion point
        for (let i = insertIdx; i < this.roots.length; i++) {
          this.roots[i].childIndex = i;
        }
      }
      countChanged = true;
    }

    // Update existing expanded built parents that may have new children
    for (const node of this.parentNodes) {
      if (!node.expanded || !node.childrenBuilt) continue;

      const smChildren = this.source.getChildren(node.symbolId);
      if (smChildren.size === node.children.length) continue; // No new children

      // Find new children
      const builtChildIds = new Set(node.children.map((c) => c.symbolId));
      for (const childId of smChildren) {
        if (builtChildIds.has(childId)) continue;

        // New child — create TreeNode
        const childChildIds = this.source.getChildren(childId);
        const hasChildren = childChildIds.size > 0;
        const childNodeId = node.nodeId + '/' + childId;

        let expanded: boolean;
        if (this._expandAllMode) {
          expanded = hasChildren ? !this.restoredCollapsed.has(childNodeId) : false;
        } else {
          expanded = hasChildren ? this.restoredExpanded.has(childNodeId) : false;
        }

        const childNode: TreeNode = {
          symbolId: childId,
          nodeId: childNodeId,
          level: node.level + 1,
          parent: node,
          children: [],
          childrenBuilt: false,
          childIndex: 0,
          expanded,
          hasChildren,
          renderNodeCount: 1,
          matchesFilter: false,
          _visibleCache: null,
        };

        if (expanded && hasChildren && this._expandAllMode) {
          childNode.renderNodeCount = this.source.getSubtreeSize(childId);
        }

        this.nodeBySymbolId.set(childId, childNode);
        this.nodeByNodeId.set(childNodeId, childNode);
        if (hasChildren) this.parentNodes.add(childNode);
        node.children.push(childNode);
      }

      // Re-sort children
      node.children.sort(comparator);
      for (let i = 0; i < node.children.length; i++) {
        node.children[i].childIndex = i;
      }

      // Recompute this node's count
      this.recomputeRenderNodeCountLazy(node);
      this.propagateCountsUp(node);
      countChanged = true;
    }

    // Also update renderNodeCounts for expand-all un-built nodes (subtreeSize may have grown)
    if (this._expandAllMode) {
      for (const node of this.parentNodes) {
        if (node.expanded && !node.childrenBuilt) {
          const newSize = this.source.getSubtreeSize(node.symbolId);
          if (node.renderNodeCount !== newSize) {
            node.renderNodeCount = newSize;
            countChanged = true;
          }
        }
      }
      // Update roots that might have changed subtreeSize
      for (const root of this.roots) {
        if (root.expanded && !root.childrenBuilt) {
          const newSize = this.source.getSubtreeSize(root.symbolId);
          if (root.renderNodeCount !== newSize) {
            root.renderNodeCount = newSize;
            countChanged = true;
          }
        }
      }
    }

    // Check for parents that weren't parents before (hasChildren may have changed)
    for (const [symbolId, node] of this.nodeBySymbolId) {
      if (!node.hasChildren) {
        const childIds = this.source.getChildren(symbolId);
        if (childIds.size > 0) {
          node.hasChildren = true;
          this.parentNodes.add(node);
          node._visibleCache = null;
          countChanged = true;
        }
      }
    }

    if (countChanged) {
      this.recomputeTotalVisibleFromRoots();
      this.invalidateCursor();
      this.emitViewChanged({ type: 'full', visibleCount: this.totalVisibleCount });
    }
  }

  // ============================================================
  // Core: renderNodeCount Computation (Lazy-aware)
  // ============================================================

  /**
   * Recompute renderNodeCount for a node. Handles lazy (un-built) children
   * by using subtreeSize from the source.
   */
  private recomputeRenderNodeCountLazy(node: TreeNode): number {
    // Leaf or collapsed: just self
    if (!node.hasChildren || !node.expanded) {
      node.renderNodeCount = 1;
      return 1;
    }

    // Expanded but children not built (expand-all mode): use subtreeSize
    if (!node.childrenBuilt) {
      node.renderNodeCount = this.source.getSubtreeSize(node.symbolId);
      return node.renderNodeCount;
    }

    // Children built: sum them up
    let count = 1;
    for (const child of node.children) {
      count += this.recomputeRenderNodeCountLazy(child);
    }
    node.renderNodeCount = count;
    return count;
  }

  /**
   * Recompute totalVisibleCount from root renderNodeCounts. O(R).
   */
  private recomputeTotalVisibleFromRoots(): void {
    this.totalVisibleCount = 0;
    for (const root of this.roots) {
      this.totalVisibleCount += root.renderNodeCount;
    }
  }

  /**
   * After toggling a single node, propagate renderNodeCount changes up to root.
   */
  private propagateRenderNodeCountUp(node: TreeNode): void {
    this.propagateCountsUp(node);
    this.recomputeTotalVisibleFromRoots();
  }

  /**
   * Propagate renderNodeCount changes from a node up to the root.
   * Stops early if no change detected.
   */
  private propagateCountsUp(node: TreeNode): void {
    let current = node.parent;
    while (current) {
      let count: number;

      if (!current.expanded || !current.hasChildren) {
        count = 1;
      } else if (!current.childrenBuilt) {
        // Un-built expanded: keep subtreeSize
        break;
      } else {
        count = 1;
        for (const child of current.children) {
          count += child.renderNodeCount;
        }
      }

      if (current.renderNodeCount === count) break;
      current.renderNodeCount = count;
      current = current.parent;
    }
  }

  // ============================================================
  // Core: Index Lookup (tree walk with lazy building)
  // ============================================================

  /**
   * Find the TreeNode at a given visible index by walking the tree.
   * Triggers ensureChildrenBuilt() when walking into un-built expanded nodes.
   */
  private findNodeAtIndex(globalIndex: number): TreeNode | null {
    let remaining = globalIndex;

    for (const root of this.roots) {
      if (root.renderNodeCount === 0) continue;
      if (remaining < root.renderNodeCount) {
        return this.walkToIndex(root, remaining);
      }
      remaining -= root.renderNodeCount;
    }

    return null;
  }

  /**
   * Walk into a node's subtree to find the item at localIndex.
   * Builds children on demand when they haven't been materialized yet.
   */
  private walkToIndex(node: TreeNode, localIndex: number): TreeNode {
    // Index 0 = the node itself
    if (localIndex === 0) return node;

    // Need to descend into children — build them if needed
    if (!node.childrenBuilt && node.hasChildren && node.expanded) {
      this.ensureChildrenBuilt(node);
    }

    let remaining = localIndex - 1;
    for (const child of node.children) {
      if (child.renderNodeCount === 0) continue;
      if (remaining < child.renderNodeCount) {
        return this.walkToIndex(child, remaining);
      }
      remaining -= child.renderNodeCount;
    }

    // Shouldn't reach here if renderNodeCount is correct
    return node;
  }

  /**
   * Compute the global visible index of a node by walking up to root.
   */
  private getIndexOfNode(node: TreeNode): number {
    let index = 0;
    let current: TreeNode = node;

    while (current.parent) {
      // Add 1 for the parent itself
      index += 1;
      // Sum preceding siblings' renderNodeCount
      const siblings = current.parent.children;
      for (let i = 0; i < current.childIndex; i++) {
        index += siblings[i].renderNodeCount;
      }
      current = current.parent;
    }

    // At root level: sum preceding roots
    for (let i = 0; i < current.childIndex; i++) {
      index += this.roots[i].renderNodeCount;
    }

    return index;
  }

  /**
   * Check if a node is actually visible (all ancestors expanded).
   */
  private isNodeVisible(node: TreeNode): boolean {
    if (node.renderNodeCount === 0) return false;
    let current = node.parent;
    while (current) {
      if (!current.expanded) return false;
      if (current.renderNodeCount === 0) return false;
      current = current.parent;
    }
    return true;
  }

  // ============================================================
  // Core: DFS Successor (for sequential getRange)
  // ============================================================

  /**
   * Find the next visible node in DFS order after the given node.
   * Builds children on demand for expanded un-built nodes.
   */
  private dfsSuccessor(node: TreeNode): TreeNode | null {
    // If expanded and has children, first child
    if (node.expanded && node.hasChildren) {
      // Build children on demand
      if (!node.childrenBuilt) {
        this.ensureChildrenBuilt(node);
      }
      for (const child of node.children) {
        if (child.renderNodeCount > 0) {
          return child;
        }
      }
    }

    // Otherwise, next visible sibling or ancestor's next sibling
    let current: TreeNode | null = node;
    while (current) {
      const next = this.nextVisibleSibling(current);
      if (next) return next;
      current = current.parent;
    }

    return null;
  }

  /**
   * Find the next visible sibling of a node.
   */
  private nextVisibleSibling(node: TreeNode): TreeNode | null {
    if (!node.parent) {
      for (let i = node.childIndex + 1; i < this.roots.length; i++) {
        if (this.roots[i].renderNodeCount > 0) return this.roots[i];
      }
      return null;
    }

    const siblings = node.parent.children;
    for (let i = node.childIndex + 1; i < siblings.length; i++) {
      if (siblings[i].renderNodeCount > 0) return siblings[i];
    }
    return null;
  }

  private invalidateCursor(): void {
    this.cursorValid = false;
    this.cursor = null;
  }

  // ============================================================
  // Core: Flat Filter Mode
  // ============================================================

  /**
   * Compute flat filter results from the data source.
   * No TreeNodes needed — scans the flat symbols iterator directly.
   * O(N) scan + O(M * D) sort-path construction where M = matches, D = avg depth.
   */
  private computeFlatFilterResults(prevResults?: FilterResult[] | null): FilterResult[] {
    if (!this.parsedFilter) return [];

    const src = this.source;
    const ctx = this.filterCtx;

    const results: FilterResult[] = [];

    // If we have previous results and query is a refinement, only check those
    const isRefinement =
      prevResults &&
      prevResults.length > 0 &&
      this.filterQuery.startsWith(
        // Check if this query extends the previous one
        this.filterQuery.slice(0, -1), // rough check — prev query would be shorter
      );

    const candidates = isRefinement && prevResults ? prevResults.map((r) => r.symbolId) : null;

    // Build the candidate iteration: either prior matches (refinement) or every symbol.
    const symbolsToCheck: Iterable<{ symbol_id: string; type: string; permission: string }> = candidates
      ? candidates
          .map((id) => {
            const sym = src.getSymbol(id);
            return sym ? { symbol_id: id, type: sym.type, permission: sym.permission } : null;
          })
          .filter((e): e is { symbol_id: string; type: string; permission: string } => e !== null)
      : src.iterateSymbols();

    for (const entry of symbolsToCheck) {
      const symbolId = entry.symbol_id;
      const sym = src.getSymbol(symbolId);
      if (!sym) continue;

      const depth = src.getDepth(symbolId);
      const label = src.getLabel(symbolId);
      const value = src.getValue(symbolId);
      const parentId = src.getParent(symbolId);
      const hasChildren = src.getChildren(symbolId).size > 0;

      const flatSym: FlatSymbol = {
        id: symbolId,
        symbol_id: symbolId,
        type: sym.type || 'string',
        permission: sym.permission || 'r',
        parentId: parentId ?? null,
        level: depth,
        hasChildren,
        isExpanded: false,
        isVisible: true,
        path: symbolId.split('.'), // approximate path for filter matching
        formattedPermission: formatPermission(sym.permission || 'r'),
        label,
        value,
        enumValues: sym.enumValues,
      };

      if (matchesFilters(flatSym, this.parsedFilter!, ctx)) {
        results.push({
          symbolId,
          depth,
          sortPath: this.buildSortPath(symbolId),
        });
      }
    }

    // Sort by column if active, otherwise by DFS order (sortPath)
    results.sort((a, b) => naturalSort(a.sortPath, b.sortPath));

    return results;
  }

  /**
   * Build a sort path for DFS ordering by walking parent pointers. O(depth).
   */
  private buildSortPath(symbolId: string): string {
    const segments: string[] = [];
    let current: string | undefined = symbolId;
    while (current) {
      segments.unshift(current);
      current = this.source.getParent(current);
    }
    return segments.join('/');
  }

  /**
   * Convert a filter result to VisibleSymbol for the grid.
   */
  private getFilterResultAsVisibleSymbol(index: number): VisibleSymbol | undefined {
    if (!this.filterResults || index < 0 || index >= this.filterResults.length) return undefined;
    const r = this.filterResults[index];
    const hasChildren = this.source.getChildren(r.symbolId).size > 0;

    return {
      symbolId: r.symbolId,
      nodeId: r.symbolId,
      level: r.depth,
      hasChildren,
      isExpanded: false, // Never expanded in flat filter mode
    };
  }

  /**
   * Build symbolId→index map recursively for built nodes only.
   * Un-built subtrees get no entries (they have no pool rows).
   */
  private buildIndexMapRecursive(node: TreeNode, map: Map<string, number>, startIndex: number): void {
    map.set(node.symbolId, startIndex);

    if (!node.expanded || !node.hasChildren || !node.childrenBuilt) return;

    let childStart = startIndex + 1;
    for (const child of node.children) {
      if (child.renderNodeCount > 0) {
        this.buildIndexMapRecursive(child, map, childStart);
        childStart += child.renderNodeCount;
      }
    }
  }

  // ============================================================
  // Helpers
  // ============================================================

  private emitViewChanged(event: ViewChangedEvent): void {
    this.emit('viewChanged', event);
  }

  // ============================================================
  // State Persistence
  // ============================================================

  private get storagePrefix(): string {
    return `symbolTree_${this.controllerId}`;
  }

  private saveState(): void {
    try {
      localStorage.setItem(`${this.storagePrefix}_expandAllMode`, String(this._expandAllMode));

      if (this._expandAllMode) {
        const collapsed: string[] = [];
        for (const node of this.parentNodes) {
          if (!node.expanded) {
            collapsed.push(node.nodeId);
          }
        }
        if (collapsed.length > 0) {
          localStorage.setItem(`${this.storagePrefix}_collapsedNodes`, JSON.stringify(collapsed));
        } else {
          localStorage.removeItem(`${this.storagePrefix}_collapsedNodes`);
        }
        localStorage.removeItem(`${this.storagePrefix}_expandedNodes`);
      } else {
        const expanded: string[] = [];
        for (const node of this.parentNodes) {
          if (node.expanded) {
            expanded.push(node.nodeId);
          }
        }
        if (expanded.length > 0) {
          localStorage.setItem(`${this.storagePrefix}_expandedNodes`, JSON.stringify(expanded));
        } else {
          localStorage.removeItem(`${this.storagePrefix}_expandedNodes`);
        }
        localStorage.removeItem(`${this.storagePrefix}_collapsedNodes`);
      }

      if (this.filterQuery) {
        localStorage.setItem(`${this.storagePrefix}_searchFilter`, this.filterQuery);
      } else {
        localStorage.removeItem(`${this.storagePrefix}_searchFilter`);
      }

      if (this.sortColumn) {
        localStorage.setItem(`${this.storagePrefix}_sortColumn`, this.sortColumn);
        localStorage.setItem(`${this.storagePrefix}_sortDirection`, this.sortDirection);
      } else {
        localStorage.removeItem(`${this.storagePrefix}_sortColumn`);
        localStorage.removeItem(`${this.storagePrefix}_sortDirection`);
      }
    } catch {
      // localStorage may be full or unavailable
    }
  }

  private restoreState(): void {
    try {
      const expandAll = localStorage.getItem(`${this.storagePrefix}_expandAllMode`);
      if (expandAll !== null) {
        this._expandAllMode = expandAll !== 'false';
      }

      if (this._expandAllMode) {
        const collapsed = localStorage.getItem(`${this.storagePrefix}_collapsedNodes`);
        if (collapsed) {
          const arr = JSON.parse(collapsed) as string[];
          this.restoredCollapsed = new Set(arr);
        }
      } else {
        const nodes = localStorage.getItem(`${this.storagePrefix}_expandedNodes`);
        if (nodes) {
          const arr = JSON.parse(nodes) as string[];
          this.restoredExpanded = new Set(arr);
        }
      }

      const filter = localStorage.getItem(`${this.storagePrefix}_searchFilter`);
      if (filter) {
        this.filterQuery = filter;
        this.parsedFilter = parseSearchInput(filter);
        // filterResults will be computed during buildRoots/fullRebuild
      }

      const sortCol = localStorage.getItem(`${this.storagePrefix}_sortColumn`);
      if (sortCol) {
        this.sortColumn = sortCol;
        const sortDir = localStorage.getItem(`${this.storagePrefix}_sortDirection`);
        if (sortDir === 'desc') {
          this.sortDirection = 'desc';
        } else {
          this.sortDirection = 'asc';
        }
      }
    } catch {
      // Ignore parse errors
    }
  }
}

// ============================================================
// Utility
// ============================================================

/** Pre-computed sort key — avoids per-comparison source lookups */
interface SortKey {
  str: string;
  num: number;
  empty: boolean;
  typeGroup: number;
}

function toVisibleSymbol(node: TreeNode): VisibleSymbol {
  let cached = node._visibleCache;
  if (!cached || cached.isExpanded !== node.expanded) {
    cached = {
      symbolId: node.symbolId,
      nodeId: node.nodeId,
      level: node.level,
      hasChildren: node.hasChildren,
      isExpanded: node.expanded,
    };
    node._visibleCache = cached;
  }
  return cached;
}

function formatPermission(perm: string): string {
  switch (perm) {
    case 'r':
      return 'Read';
    case 'w':
      return 'Write';
    case 'rw':
      return 'Read & Write';
    default:
      return perm;
  }
}

/** Normalize type string — merge 'integer' into 'number' for sorting */
function normalizeType(type: string | undefined): string {
  const t = (type || '').toLowerCase();
  return t === 'integer' ? 'number' : t;
}

/** Sort-priority for value grouping: numbers first, then bools, strings, arrays, objects */
function typeGroupOrder(type: string | undefined): number {
  switch (normalizeType(type)) {
    case 'number':
      return 0;
    case 'boolean':
    case 'bool':
      return 1;
    case 'string':
      return 2;
    case 'array':
      return 3;
    case 'object':
      return 4;
    default:
      return 5;
  }
}

/**
 * Natural sort comparator — handles numeric segments within strings.
 * "DB_2" < "DB_10", "motor1" < "motor12"
 */
function naturalSort(a: string, b: string): number {
  const ax: (string | number)[] = [];
  const bx: (string | number)[] = [];

  a.replace(/(\d+)|(\D+)/g, (_, d, s) => {
    ax.push(d ? +d : s);
    return '';
  });
  b.replace(/(\d+)|(\D+)/g, (_, d, s) => {
    bx.push(d ? +d : s);
    return '';
  });

  const len = Math.max(ax.length, bx.length);
  for (let i = 0; i < len; i++) {
    const ai = ax[i] ?? '';
    const bi = bx[i] ?? '';
    if (typeof ai === 'number' && typeof bi === 'number') {
      if (ai !== bi) return ai - bi;
    } else {
      const cmp = String(ai).localeCompare(String(bi), undefined, { sensitivity: 'base' });
      if (cmp !== 0) return cmp;
    }
  }
  return 0;
}
